export interface PokemonType {
  name: string;
  color: string;
}

export const POKEMON_TYPES: PokemonType[] = [
  { name: "Normal", color: "#A8A878" },
  { name: "Fire", color: "#F08030" },
  { name: "Water", color: "#6890F0" },
  { name: "Electric", color: "#F8D030" },
  { name: "Grass", color: "#78C850" },
  { name: "Ice", color: "#98D8D8" },
  { name: "Fighting", color: "#C03028" },
  { name: "Poison", color: "#A040A0" },
  { name: "Ground", color: "#E0C068" },
  { name: "Flying", color: "#A890F0" },
  { name: "Psychic", color: "#F85888" },
  { name: "Bug", color: "#A8B820" },
  { name: "Rock", color: "#B8A038" },
  { name: "Ghost", color: "#705898" },
  { name: "Dragon", color: "#7038F8" },
  { name: "Dark", color: "#705848" },
  { name: "Steel", color: "#B8B8D0" },
  { name: "Fairy", color: "#EE99AC" },
];

export type TypeEffectiveness = Record<string, number>;

export const TYPE_CHART: Record<string, TypeEffectiveness> = {
  Normal: {
    Rock: 0.5,
    Ghost: 0,
    Steel: 0.5,
  },
  Fire: {
    Fire: 0.5,
    Water: 0.5,
    Grass: 2,
    Ice: 2,
    Bug: 2,
    Rock: 0.5,
    Dragon: 0.5,
    Steel: 2,
  },
  Water: {
    Fire: 2,
    Water: 0.5,
    Grass: 0.5,
    Ground: 2,
    Rock: 2,
    Dragon: 0.5,
  },
  Electric: {
    Water: 2,
    Electric: 0.5,
    Grass: 0.5,
    Ground: 0,
    Flying: 2,
    Dragon: 0.5,
  },
  Grass: {
    Fire: 0.5,
    Water: 2,
    Grass: 0.5,
    Poison: 0.5,
    Ground: 2,
    Flying: 0.5,
    Bug: 0.5,
    Rock: 2,
    Dragon: 0.5,
    Steel: 0.5,
  },
  Ice: {
    Fire: 0.5,
    Water: 0.5,
    Grass: 2,
    Ice: 0.5,
    Ground: 2,
    Flying: 2,
    Dragon: 2,
    Steel: 0.5,
  },
  Fighting: {
    Normal: 2,
    Ice: 2,
    Poison: 0.5,
    Flying: 0.5,
    Psychic: 0.5,
    Bug: 0.5,
    Rock: 2,
    Ghost: 0,
    Dark: 2,
    Steel: 2,
    Fairy: 0.5,
  },
  Poison: {
    Grass: 2,
    Poison: 0.5,
    Ground: 0.5,
    Rock: 0.5,
    Ghost: 0.5,
    Steel: 0,
    Fairy: 2,
  },
  Ground: {
    Fire: 2,
    Electric: 2,
    Grass: 0.5,
    Poison: 2,
    Flying: 0,
    Bug: 0.5,
    Rock: 2,
    Steel: 2,
  },
  Flying: {
    Electric: 0.5,
    Grass: 2,
    Fighting: 2,
    Bug: 2,
    Rock: 0.5,
    Steel: 0.5,
  },
  Psychic: {
    Fighting: 2,
    Poison: 2,
    Psychic: 0.5,
    Dark: 0,
    Steel: 0.5,
  },
  Bug: {
    Fire: 0.5,
    Grass: 2,
    Fighting: 0.5,
    Poison: 0.5,
    Flying: 0.5,
    Psychic: 2,
    Ghost: 0.5,
    Dark: 2,
    Steel: 0.5,
    Fairy: 0.5,
  },
  Rock: {
    Fire: 2,
    Ice: 2,
    Fighting: 0.5,
    Ground: 0.5,
    Flying: 2,
    Bug: 2,
    Steel: 0.5,
  },
  Ghost: {
    Normal: 0,
    Psychic: 2,
    Ghost: 2,
    Dark: 0.5,
  },
  Dragon: {
    Dragon: 2,
    Steel: 0.5,
    Fairy: 0,
  },
  Dark: {
    Fighting: 0.5,
    Psychic: 2,
    Ghost: 2,
    Dark: 0.5,
    Fairy: 0.5,
  },
  Steel: {
    Fire: 0.5,
    Water: 0.5,
    Electric: 0.5,
    Ice: 2,
    Rock: 2,
    Steel: 0.5,
    Fairy: 2,
  },
  Fairy: {
    Fire: 0.5,
    Fighting: 2,
    Poison: 0.5,
    Dragon: 2,
    Dark: 2,
    Steel: 0.5,
  },
};

export function getTypeColor(typeName: string): string {
  const type = POKEMON_TYPES.find((t) => t.name === typeName);
  return type?.color || "#777777";
}

export function getEffectiveness(
  attackingType: string,
  defendingType: string
): number {
  return TYPE_CHART[defendingType]?.[attackingType] || 1;
}

export function getSuperEffective(typeName: string): string[] {
  const chart = TYPE_CHART[typeName];
  if (!chart) return [];
  return Object.keys(chart).filter((key) => chart[key] === 2);
}

export function getNotVeryEffective(typeName: string): string[] {
  const chart = TYPE_CHART[typeName];
  if (!chart) return [];
  return Object.keys(chart).filter((key) => chart[key] === 0.5);
}

export function getNoEffect(typeName: string): string[] {
  const chart = TYPE_CHART[typeName];
  if (!chart) return [];
  return Object.keys(chart).filter((key) => chart[key] === 0);
}

export function needsDarkText(color: string): boolean {
  // Convert hex to RGB
  const hex = color.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calculate relative luminance (WCAG formula)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // If luminance is high (bright color), use dark text
  return luminance > 0.5;
}
